import React, { useEffect, useMemo, useState } from 'react';
import { PencilLine, Save } from 'lucide-react';
import { BoholMap } from '../components/shared/BoholMap';
import { ModalShell } from '../components/shared/ModalShell';
import { mapsAPI } from '../services/api';
import { normalizeMapRecord } from '../utils/mapCatalog';

const EMPTY_MAP = {
  id: 0,
  category: 'municipality',
  map_key: '',
  title: '',
  subtitle: '',
  municipality: '',
  description: '',
  asset_path: '',
  image_base64: '',
  image_mime_type: '',
  aliasesText: '',
  sort_order: 0,
};

const CATEGORY_OPTIONS = [
  { label: 'Municipality', value: 'municipality' },
  { label: 'Bohol Province', value: 'bohol' },
  { label: 'Cacao Requirements', value: 'requirements' },
  { label: 'Sampling Site', value: 'sampling_site' },
];

function toFormValues(record) {
  if (!record) {
    return { ...EMPTY_MAP };
  }

  const normalized = normalizeMapRecord(record);
  return {
    id: normalized.id || 0,
    category: normalized.category || 'municipality',
    map_key: normalized.key || '',
    title: normalized.title || '',
    subtitle: normalized.subtitle || '',
    municipality: normalized.municipality || '',
    description: normalized.description || '',
    asset_path: normalized.rawAssetPath || '',
    image_base64: normalized.imageBase64 || '',
    image_mime_type: normalized.imageMimeType || '',
    aliasesText: (normalized.aliases || []).join(', '),
    sort_order: normalized.sortOrder || 0,
  };
}

function dataUrlToPayload(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return { image_base64: '', image_mime_type: '' };
  }

  const marker = ';base64,';
  const markerIndex = dataUrl.indexOf(marker);
  if (markerIndex === -1) {
    return { image_base64: '', image_mime_type: '' };
  }

  return {
    image_mime_type: dataUrl.slice(5, markerIndex) || 'image/jpeg',
    image_base64: dataUrl.slice(markerIndex + marker.length),
  };
}

export function AdminMap() {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [formState, setFormState] = useState(EMPTY_MAP);

  const normalizedOptions = useMemo(
    () => catalog
            .map((record) => normalizeMapRecord(record))
            .filter((record) => record.category === formState.category),
    [catalog, formState.category]
  );

  const selectedOption = useMemo(
    () => normalizedOptions.find(
      (item) => (item.municipality || '').trim().toLowerCase() === (formState.municipality || '').trim().toLowerCase()
    ),
    [normalizedOptions, formState.municipality]
  );

  const loadCatalog = async () => {
    try {
      const response = await mapsAPI.getCatalog();
      const nextCatalog = Array.isArray(response.data) ? response.data : [];
      setCatalog(nextCatalog);
    } catch (loadError) {
      setCatalog([]);
      setStatus(loadError.message || 'Failed to load map catalog.');
    }
  };

  useEffect(() => {
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (catalog.length === 0) return;
    const exists = normalizedOptions.find(
      (item) => (item.municipality || '').trim().toLowerCase() === (formState.municipality || '').trim().toLowerCase()
    );
    if (!exists && normalizedOptions.length > 0) {
      const first = toFormValues(normalizedOptions[0]);
      setFormState((prev) => ({
        ...prev,
        ...first,
        category: formState.category,
        image_base64: '',
        image_mime_type: '',
      }));
    }
  }, [formState.category, catalog, normalizedOptions]);

  const openEditor = () => {
    const first = normalizedOptions[0];
    const initial = first ? toFormValues(first) : EMPTY_MAP;
    setFormState({
      ...initial,
      category: formState.category,
      image_base64: '',
      image_mime_type: '',
    });
    setStatus('');
    setIsEditorOpen(true);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    
    if (name === 'municipality') {
      const existing = catalog.find((c) => c.municipality === value);
      if (existing) {
        const formVals = toFormValues(existing);
        setFormState((current) => ({
          ...current,
          ...formVals,
          category: current.category,
          image_base64: '',
          image_mime_type: '',
        }));
        return;
      }
    }
    
    setFormState((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStatus('Compressing image...');
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1080;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        const { image_base64, image_mime_type } = dataUrlToPayload(dataUrl);
        
        setFormState((current) => ({
          ...current,
          image_base64,
          image_mime_type,
        }));
        setStatus(`Selected image: ${file.name}`);
      };
      img.src = String(e.target.result || '');
    };
    reader.onerror = () => setStatus('Failed to read selected image file.');
    reader.readAsDataURL(file);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const municipality = (formState.municipality || '').trim();

    if (!municipality) {
      setStatus('Please select an item.');
      return;
    }

    if (!formState.image_base64) {
      setStatus('Please upload an image first.');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        id: selectedOption?.id || formState.id || 0,
        category: formState.category,
        municipality,
        image_base64: formState.image_base64,
        image_mime_type: formState.image_mime_type || 'image/jpeg',
      };

      await mapsAPI.saveCatalog(payload);
      setStatus('Map image updated successfully.');
      await loadCatalog();
      setFormState((current) => ({
        ...current,
        image_base64: '',
        image_mime_type: '',
      }));
    } catch (saveError) {
      setStatus(saveError.message || 'Failed to update map.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <BoholMap
        catalog={catalog}
        headerAction={(
          <button
            type="button"
            onClick={openEditor}
            className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-amber-700"
          >
            <PencilLine className="h-4 w-4" />
            Update Map
          </button>
        )}
      />

      {isEditorOpen && (
        <ModalShell
          title="Update Map"
          subtitle={<p className="text-sm text-slate-600">Choose category and municipality, then upload a new image.</p>}
          onClose={() => setIsEditorOpen(false)}
          maxWidth="max-w-3xl"
        >
          <form onSubmit={handleSave} className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-medium text-slate-700">
                Category
                <select
                  name="category"
                  value={formState.category}
                  onChange={handleFieldChange}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-700">
                {formState.category === 'municipality' ? 'Municipality' : 'Map Item Name'}
                <select
                  name="municipality"
                  value={formState.municipality}
                  onChange={handleFieldChange}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                >
                  <option value="">-- Select Item --</option>
                  {catalog.map((item, idx) => (
                    <option key={idx} value={item.municipality}>
                      {item.municipality}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-700 md:col-span-2">
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none file:mr-3 file:rounded-full file:border-0 file:bg-amber-100 file:px-3 file:py-1 file:text-amber-900"
                />
              </label>
            </div>

            {(selectedOption?.imageBase64 || (selectedOption?.id && selectedOption?.category)) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Current Image</div>
                <img
                  src={
                    selectedOption.imageBase64 
                      ? `data:${selectedOption.imageMimeType || 'image/jpeg'};base64,${selectedOption.imageBase64}`
                      : `${(process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '')}/maps/catalog/${encodeURIComponent(selectedOption.category)}/${encodeURIComponent(selectedOption.id)}/image`
                  }
                  alt={selectedOption.title || 'Map image'}
                  className="h-auto max-h-[280px] w-full rounded-lg border border-slate-200 object-contain bg-white"
                />
              </div>
            )}

            {!selectedOption && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                No map found for this category and municipality combination.
              </div>
            )}

            {status && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {status}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Updating...' : 'Update Map'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}

export default AdminMap;
